<template>
  <div v-if="isVisible" class="popup-overlay" @click.self="handleOverlayClick">
    <div class="popup-container" ref="popupRef">
      <div v-if="currentMessage" class="dialog-content">
        <div v-if="currentMessage.disposable" @click="close" class="close-button">
          <Icon class="icon" icon="ant-design:close-outlined" />
        </div>
        <img v-if="currentMessage.image" :src="currentMessage.image" class="message-image" />
        <div class="subject" v-html="currentMessage.subject"></div>
        <div class="content" v-html="currentMessage.content" @click="clickedContent"></div>
        <button class="btn" v-if="!currentMessage.showForEver" @click="markAsReadAndClose">
          {{ $t('common.close') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { readMessage } from '@/api/backoffice';
import { actionTypes as configActionTypes } from '@/store/modules/config';
import { getItem } from '@/helpers/storage';

const CLOSE_POPUP_DATA_ATTR = 'data-close-popup';
const alreadyShown = new Set();

export default {
  name: 'PopupContainer',
  data() {
    return {
      isVisible: false,
    };
  },
  computed: {
    ...mapState({
      messages: (state) => state.config?.messages,
      isLoggedIn: (state) => state.auth?.isLoggedIn,
      token: (state) => state.config?.token,
    }),
    currentMessage() {
      if (!this.messages?.length) {
        return null;
      }

      const message = this.messages[0];

      if (alreadyShown.has(message.id)) {
        return null;
      }

      alreadyShown.add(message.id);
      return message;
    },
  },
  watch: {
    isLoggedIn: {
      immediate: true,
      async handler(value) {
        const token = getItem('accessToken');

        if (token) {
          this.$store.dispatch(configActionTypes.initializeToken);
        }

        if (value && token) {
          await this.$store.dispatch(configActionTypes.fetchClientMessages);
        }
      },
    },
    currentMessage: {
      immediate: true,
      deep: true,
      handler(value) {
        if (value) {
          this.openModal();
        } else {
          this.closeModal();
        }
      },
    },
  },
  methods: {
    close() {
      this.closeModal();
    },

    async markAsReadAndClose() {
      if (this.currentMessage?.messageId) {
        await readMessage(this.token, this.currentMessage.messageId);
        await this.$store.dispatch(configActionTypes.fetchClientMessages);
      }
      this.close();
    },

    clickedContent(e) {
      if (e.target?.hasAttribute(CLOSE_POPUP_DATA_ATTR)) {
        this.close();
      }
    },

    handleOverlayClick(e) {
      if (e.target === e.currentTarget && this.currentMessage?.disposable) {
        this.close();
      }
    },

    openModal() {
      this.isVisible = true;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';

      if (this.currentMessage?.showForEver) {
        document.addEventListener('keydown', this.preventEscClose);
      } else {
        document.addEventListener('keydown', this.handleEscClose);
      }
    },

    closeModal() {
      this.isVisible = false;
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';

      document.removeEventListener('keydown', this.preventEscClose);
      document.removeEventListener('keydown', this.handleEscClose);
    },

    preventEscClose(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
      }
    },

    handleEscClose(event) {
      if (event.key === 'Escape') {
        this.close();
      }
    },
  },

  beforeUnmount() {
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', this.preventEscClose);
    document.removeEventListener('keydown', this.handleEscClose);
  },
};
</script>

<style scoped>
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.popup-container {
  position: relative;
  min-width: 400px;
  max-width: 90vw;
  max-height: 90vh;
  padding: 30px;
  border: none;
  outline: none;
  border-radius: 5px;
  background-color: rgb(26, 24, 24);
  color: white;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  overflow-y: auto;
}

.dialog-content {
  padding: 10px;
  position: relative;
}

.close-button {
  position: absolute;
  right: 10px;
  top: 10px;
  cursor: pointer;
  z-index: 1;
}

.icon {
  font-size: 20px;
  color: white;
  transition: color 0.2s ease;
}

.icon:hover {
  color: #dc3545;
}

.message-image {
  max-width: 100%;
  height: auto;
  margin-bottom: 15px;
  border-radius: 4px;
}

.subject {
  font-size: 1.2em;
  font-weight: bold;
  margin-bottom: 15px;
  line-height: 1.4;
}

.content {
  margin-bottom: 20px;
  line-height: 1.5;
}

.btn {
  margin-top: 10px;
  padding: 8px 16px;
  color: #fff;
  background-color: #dc3545;
  border: 1px solid #dc3545;
  border-radius: 0.25rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  text-align: center;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  outline: none;
}

.btn:hover {
  background-color: #c82333;
  border-color: #bd2130;
}

.btn:focus {
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.25);
}

.popup-overlay {
  animation: fadeIn 0.3s ease-out;
}

.popup-container {
  animation: slideIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@media (max-width: 768px) {
  .popup-container {
    min-width: 300px;
    padding: 20px;
    margin: 10px;
  }

  .popup-overlay {
    padding: 10px;
  }
}
</style>
